import { MenuItem, Box, Backdrop, Button, Grid, styled, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAddBahasa } from "../../../../api/effects/mutations/Bahasa/addBahasa";
import { useEditBahasa } from "../../../../api/effects/mutations/Bahasa/editBahasa";
import { useFetchBahasa } from "../../../../api/effects/mutations/Bahasa/useFetchBahasa";
import InputSelect from "../../../../components/InputSelect";
import InputTextField from "../../../../components/InputTextField";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { formatDate, convertDate, toIsoStringDate } from "../../../../utils/dateConvert";
import InputDateForm from "../../../../components/InputDateForm";
import { InputTextFieldForm } from "../../../../components/InputTextFieldForm";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import InputSelectForm from "../../../../components/InputSelectForm";


const TitleGrid = styled(Grid)(() => ({
  paddingLeft: "10%",
}));
const SquareGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: "#FFF",
  height: "auto",
  width: "90%",
  boxShadow: "1px 1px 10px #888888",
  borderRadius: "10px",
  padding: "1%",
  [theme.breakpoints.down("sm")]: {
    padding: "10px",
  },
  margin: "1%",
}));

const FullButton = styled(Button)(({ theme }) => ({
  width: "70%",
}));

const ScrollGrid = styled(Grid)(({ theme }) => ({
  overflow: "auto",
  maxHeight: "90vh",
}));

const InputWrapperGrid = styled(Grid)(() => ({
  padding: "1%",
}));

type Props = {
  open: boolean;
  handleClose: () => void;
  ID?: number;
};
type SelectionOption = {
  value: string;
  text: string;
};




type FormValues = {
  bahasa: string;
  berbicara: string;
  mendengar: string;
  menulis: string;
  membaca: string;
};
const schema = z.object({
  bahasa: z.string().min(1, "Bahasa Field is required"),
  berbicara: z.string().min(1, "Please choose level of proficiency from the following options "),
  mendengar: z.string().min(1, "Please choose level of proficiency from the following options "),
  menulis: z.string().min(1, "Please choose level of proficiency from the following options "),
  membaca: z.string().min(1, "Please choose level of proficiency from the following options "),
})
const selection = [
  {
    value: "Baik Sekali",
    text: "Baik Sekali",
  },
  {
    value: "Baik",
    text: "Baik",
  },
  {
    value: "Cukup",
    text: "Cukup",
  },
  {
    value: "Kurang",
    text: "Kurang",
  },
];

export default function BahasaModal({ ID = -1, open, handleClose }: Props) {

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormValues>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: {
      bahasa: "",
      berbicara: "",
      mendengar: "",
      menulis: "",
      membaca: ""
    },
  });

  const [selectionData, setSelectionData] = useState<SelectionOption[]>(selection);
  const { mutateAsync: GetBahasaData } = useFetchBahasa({
    onSuccess(data) {
      setValue("bahasa", data.BAHASA);
      setValue("berbicara", data.BERBICARA);
      setValue("mendengar", data.MENDENGAR);
      setValue("menulis", data.MENULIS);
      setValue("membaca", data.MEMBACA);
      console.log("Success");
    },
    onError() {
      console.log("Error");
    },
  });

  useEffect(() => {
    if (ID > 0) {
      GetBahasaData(ID);
    }
  }, [open]);

  useEffect(() => {
    setSelectionData(selection);
    if (!open) {
      reset();
    }
  }, [selection, open, reset]);

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const handleModalClose = () => {
    reset();
    handleClose();
  };


  const { mutateAsync: AddBahasa, isLoading: isAdding } = useAddBahasa({
    onSuccess() {
      handleModalClose();
      console.log("Success");
    },
    onError() {
      console.log("Error");
    },
  });

  const { mutateAsync: EditBahasa, isLoading: isEditing } = useEditBahasa({
    onSuccess() {
      handleModalClose();
      console.log("Success");
    },
    onError() {
      console.log("Error");
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    const payload = {
      BAHASA: data.bahasa,
      BERBICARA: data.berbicara,
      MENDENGAR: data.mendengar,
      MENULIS: data.menulis,
      MEMBACA: data.membaca,
    };
    try {
      if (ID > 0) {
        await EditBahasa({
          BAHASA_ID: ID,
          ...payload,
        });
      } else {
        await AddBahasa(payload);
      }
    } catch (error) {
      console.error("Submission failed:", error);
    }
  };

  return (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={open}
    >
      <Box component={"form"} onSubmit={handleSubmit(onSubmit)} noValidate>
        <SquareGrid container>
          <ScrollGrid container>
            <Grid container justifyContent="flex-start" paddingLeft={"10.5%"} item xs={6}
              paddingBottom={
                {
                  xs: 5,
                  sm: 5,
                  md: 5,
                  lg: 5,
                  xl: 5
                }}>
              <Typography variant="subtitle1" color="black">
                {ID === -1 ? "Tambah Keahlian Bahasa" : "Ubah Keahlian Bahasa"}
              </Typography>
            </Grid>
            <Grid container justifyContent="flex-end" item xs={6}>
              <Button onClick={handleClose}>Close</Button>
            </Grid>
            {/* Bahasa - Text Field */}
            <InputTextFieldForm
              name="bahasa"
              control={control}
              label="Bahasa"
              error={errors.bahasa}
            />
            {/* Berbicara - Select */}
            <InputSelectForm
              name="berbicara"
              title="Berbicara"
              control={control}
              options={selectionData}
              placeholder="-- Pilih tingkat keahlian --"
            />

            {/* Mendengar - Select */}
            <InputSelectForm
              name="mendengar"
              title="Mendengar"
              control={control}
              options={selectionData}
              placeholder="-- Pilih tingkat keahlian --"
            />

            {/* Menulis - Select */}
            <InputSelectForm
              name="menulis"
              title="Menulis"
              control={control}
              options={selectionData}
              placeholder="-- Pilih tingkat keahlian --"
            />
            {/* Membaca - Select */}
            <InputSelectForm
              name="membaca"
              title="Membaca"
              control={control}
              options={selectionData}
              placeholder="-- Pilih tingkat keahlian --"
            />

            <Grid container justifyContent="center" item xs={12}
              paddingTop={{
                xs: 2,
                sm: 2,
                md: 2,
                lg: 2,
                xl: 2
              }}
              paddingBottom={{
                xs: 2,
                sm: 2,
                md: 2,
                lg: 2,
                xl: 2
              }}>
              <FullButton variant="contained" type="submit" disabled={isAdding || isEditing}>
                {(isAdding || isEditing) ? "Saving..." : "Save"}
              </FullButton>
            </Grid>
          </ScrollGrid>
        </SquareGrid>
      </Box>
    </Backdrop>
  );
}
